import { html, render } from 'lit-html';
import { appStore, actions } from '../store/appStore.js';
import menuIcon from '../icons/menu.svg';
import expandIcon from '../icons/expand.svg';
import collapseIcon from '../icons/collapse.svg';
import './NavigationMenu.js';
import { handleSelectFocus } from '../utils/selectUtils.js';
import { dataService } from '../services/dataService.js';

export class PackStatusHeader extends HTMLElement {
  private unsubscribe?: () => void;

  connectedCallback() {
    this.unsubscribe = appStore.subscribe(() => {
      this.render();
    });
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  private handleFacilityChange = async (event: Event) => {
    const select = event.target as HTMLSelectElement;
    const facilityId = parseInt(select.value);
    const state = appStore.getState();
    const selectedFacility = state.facilities.find(f => f.id === facilityId) || null;

    actions.setSelectedFacility(selectedFacility);

    // Load data for the new facility
    if (selectedFacility) {
      await dataService.onFacilityChange(facilityId);
    }

    select.blur();
  };

  private handleExpandClick = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    actions.togglePackLinesExpanded();
  };

  private handlePackLinesClick = () => {
    actions.toggleDrawer();
  };

  private handleMenuClick = () => {
    actions.toggleMenu();
  };


  private render() {
    const state = appStore.getState();
    const { facilities, selectedFacility, currentCycle, isPackLinesExpanded } = state;

    // Only show expand button on home page
    const isHomePage = window.location.pathname === '/';

    const leftContent = html`
      <div class="relative flex-shrink-0">
        <button
          @click=${this.handleMenuClick}
          data-menu-toggle
          class="hover:bg-emerald-700 rounded p-1 flex-shrink-0"
        >
          <img src="${menuIcon}" class="w-6 h-6 flex-shrink-0" alt="Menu" />
        </button>
        <navigation-menu></navigation-menu>
      </div>
      <h1 class="text-lg sm:text-xl font-bold tracking-wide truncate">PACK STATUS</h1>
    `;

    const rightContent = html`
      <div class="flex items-center space-x-3">
        <span class="text-sm font-semibold tracking-wide">FACILITY</span>
        <select
          class="bg-white text-gray-800 pl-4 pr-10 py-2 rounded-md border-0 focus:outline-none focus:ring-2 focus:ring-emerald-400 appearance-none font-medium text-sm min-w-[200px]"
          @change=${this.handleFacilityChange}
          @focus=${handleSelectFocus}
          .value=${selectedFacility?.id || ''}
        >
          ${facilities.map(facility => html`
            <option value=${facility.id} ?selected=${facility.id === selectedFacility?.id}>
              ${facility.name}
            </option>
          `)}
        </select>
      </div>
      <div class="flex items-center space-x-3">
        <span class="text-sm font-semibold tracking-wide">CYCLE</span>
        <div class="text-white font-medium text-lg px-2 py-2">
          ${currentCycle?.name || 'Loading...'}
        </div>
      </div>

      ${isHomePage ? html`
        <button
          type="button"
          @click=${this.handleExpandClick}
          class="p-2 bg-gray-500 hover:bg-gray-600 rounded-md"
          title=${isPackLinesExpanded ? 'Show routes' : 'Hide routes'}
        >
          <img src="${isPackLinesExpanded ? collapseIcon : expandIcon}" class="w-5 h-5" alt=${isPackLinesExpanded ? 'Collapse' : 'Expand'} />
        </button>
      ` : ''}

      <button
        @click=${this.handlePackLinesClick}
        class="bg-emerald-500 hover:bg-emerald-400 px-6 py-2 rounded-md text-white font-semibold text-sm"
      >
        Pack Lines
      </button>
    `;


    const template = html`
      <header class="bg-emerald-600 text-white px-4 sm:px-6 py-4 flex items-center justify-between">
        <div class="flex items-center space-x-3 sm:space-x-6">
          ${leftContent}
        </div>

        <div class="flex items-center space-x-6">
          ${rightContent}
        </div>
      </header>
    `;

    render(template, this);
  }
}

customElements.define('pack-status-header', PackStatusHeader);
